import { useEffect, useMemo, useState } from 'react';
import { Bus, ExternalLink, MapPin, Navigation, Radio, Search } from 'lucide-react';
import L from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { trackingAPI, createAuthenticatedWebSocket } from '../services/api';

const STALE_AFTER_MS = 2 * 60 * 1000;

const getPoint = (item) => {
  const latitude = Number(item.latitude);
  const longitude = Number(item.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
};

const getAge = (recordedAt) => {
  if (!recordedAt) return null;
  return Date.now() - new Date(recordedAt).getTime();
};

const formatAge = (recordedAt) => {
  const age = getAge(recordedAt);
  if (age === null || Number.isNaN(age)) return 'No GPS';
  if (age < 60 * 1000) return 'Live now';
  if (age < 60 * 60 * 1000) return `${Math.round(age / 60000)} min ago`;
  return `${Math.round(age / 3600000)} hr ago`;
};

const isBusReader = (item) => item.checkpoint_type === 'bus';
const checkpointLabel = (item) => (isBusReader(item) ? 'Bus' : item.checkpoint_type === 'main_gate' ? 'Main Gate' : item.checkpoint_type || 'Reader');
const googleMapsUrl = (item) => `https://www.google.com/maps?q=${item.latitude},${item.longitude}`;
const appleMapsUrl = (item) => `https://maps.apple.com/?ll=${item.latitude},${item.longitude}&q=${encodeURIComponent(item.reader_name || item.reader_uid || 'Reader')}`;

const getMarkerIcon = (item) => {
  const age = getAge(item.recorded_at);
  const isLive = age !== null && age <= STALE_AFTER_MS;
  const color = isBusReader(item)
    ? isLive ? '#10b981' : '#f59e0b'
    : isLive ? '#3b82f6' : '#64748b';

  return L.divIcon({
    className: '',
    html: `<div style="
      width: 34px;
      height: 34px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 9999px;
      border: 3px solid white;
      background: ${color};
      color: white;
      font-weight: 800;
      font-size: 12px;
      box-shadow: 0 10px 24px rgba(15,23,42,.28);
    ">${isBusReader(item) ? 'B' : 'R'}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -18],
  });
};

const FitMapToMarkers = ({ locations }) => {
  const map = useMap();
  useEffect(() => {
    const points = locations.map(getPoint).filter(Boolean);
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((point) => [point.latitude, point.longitude]));
    map.fitBounds(bounds, { padding: [56, 56], maxZoom: 16 });
  }, [locations, map]);

  return null;
};

const LeafletLiveMap = ({ locations, busCount }) => {
  const center = useMemo(() => {
    const first = locations.find((item) => getPoint(item));
    const point = first ? getPoint(first) : null;
    return point ? [point.latitude, point.longitude] : [12.9716, 77.5946];
  }, [locations]);
  return (
    <div className="relative h-[520px] overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
      <MapContainer center={center} zoom={locations.length ? 14 : 11} className="h-full w-full" scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitMapToMarkers locations={locations} />
        {locations.map((item) => {
          const point = getPoint(item);
          if (!point) return null;
          return (
            <Marker
              key={item.reader_uid}
              position={[point.latitude, point.longitude]}
              icon={getMarkerIcon(item)}
            >
              <Popup>
                <div className="min-w-44">
                  <p className="font-bold text-slate-950">{item.reader_name || item.reader_uid}</p>
                  <p className="text-sm text-slate-600">{checkpointLabel(item)}</p>
                  <p className="text-sm text-slate-500">{formatAge(item.recorded_at)}</p>
                  <p className="mt-2 font-mono text-xs text-slate-500">
                    {point.latitude.toFixed(6)}, {point.longitude.toFixed(6)}
                  </p>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
      <div className="pointer-events-none absolute left-5 top-5 z-[500] rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm">
        {locations.length ? `${busCount} buses, ${locations.length} total readers on map` : 'Waiting for GPS heartbeats'}
      </div>
      {!locations.length && (
        <div className="pointer-events-none absolute inset-0 z-[400] flex items-center justify-center">
          <div className="rounded-2xl bg-white/95 px-5 py-3 text-sm font-semibold text-slate-600 shadow-sm">
            No live GPS points yet
          </div>
        </div>
      )}
    </div>
  );
};

const LiveTracking = () => {
  const [locations, setLocations] = useState([]);
  const [connected, setConnected] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    trackingAPI.getLatestReaderLocations()
      .then((res) => setLocations(res.data.locations || []))
      .catch(() => {});

    const ws = createAuthenticatedWebSocket();
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'READER_LOCATION') {
          setLocations((prev) => {
            const next = [msg.data, ...prev.filter((item) => item.reader_uid !== msg.data.reader_uid)];
            return next.sort((a, b) => new Date(b.recorded_at || 0) - new Date(a.recorded_at || 0));
          });
        }
      } catch {
      }
    };

    return () => ws.close();
  }, []);

  const query = search.trim().toLowerCase();
  const filteredLocations = locations.filter((item) => {
    if (!query) return true;
    return [item.reader_name, item.reader_uid, item.location_name, item.device_type, item.checkpoint_type]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  });

  const busReaders = locations.filter(isBusReader);
  const busWithGps = busReaders.filter((item) => getPoint(item));
  const movingBuses = busWithGps.filter((item) => {
    const age = getAge(item.recorded_at);
    return age !== null && age <= STALE_AFTER_MS;
  });
  const staleBuses = busWithGps.length - movingBuses.length;
  const noGpsBuses = busReaders.length - busWithGps.length;
  const gpsLocations = filteredLocations.filter((item) => getPoint(item));
  const liveLocations = gpsLocations.filter((item) => {
    const age = getAge(item.recorded_at);
    return age !== null && age <= STALE_AFTER_MS;
  });
  const staleLocations = gpsLocations.length - liveLocations.length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Live Tracking</h1>
          <p className="mt-1 text-sm text-slate-500">Live fleet location for all reader devices and face tablets.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm text-slate-600 shadow-sm">
            <span className={`h-2 w-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-400'}`} />
            {connected ? 'Live sync' : 'Offline'}
          </div>
          <div className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm">
            {movingBuses.length} moving buses / {busReaders.length} buses
          </div>
        </div>
      </div>

      <section className="grid gap-4 lg:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-slate-500">Bus Readers</p>
          <p className="mt-2 text-3xl font-bold text-slate-950">{busReaders.length}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-slate-500">Moving Buses</p>
          <p className="mt-2 text-3xl font-bold text-emerald-600">{movingBuses.length}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-slate-500">Stale Buses</p>
          <p className="mt-2 text-3xl font-bold text-amber-600">{staleBuses}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-slate-500">Bus GPS Missing</p>
          <p className="mt-2 text-3xl font-bold text-red-500">{noGpsBuses}</p>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-slate-500">All Readers</p>
          <p className="mt-2 text-2xl font-bold text-slate-900">{locations.length}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-slate-500">All Live GPS</p>
          <p className="mt-2 text-2xl font-bold text-emerald-600">{liveLocations.length}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-slate-500">All Stale GPS</p>
          <p className="mt-2 text-2xl font-bold text-amber-600">{staleLocations}</p>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-950">Fleet Map</h2>
            <p className="text-sm text-slate-500">Bus markers update live when each tablet sends its 30-second GPS heartbeat.</p>
          </div>
          <div className="relative w-full md:w-80">
            <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search reader, bus, location..."
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-10 pr-3 text-sm outline-none focus:border-teal-500 focus:bg-white"
            />
          </div>
        </div>

        <LeafletLiveMap locations={gpsLocations} busCount={busWithGps.length} />
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        {filteredLocations.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-400 lg:col-span-2">
            No reader matches this search.
          </div>
        ) : [...filteredLocations].sort((a, b) => Number(isBusReader(b)) - Number(isBusReader(a))).map((item) => {
          const hasGps = Number.isFinite(Number(item.latitude)) && Number.isFinite(Number(item.longitude));
          const mapsUrl = hasGps ? googleMapsUrl(item) : null;
          const appleUrl = hasGps ? appleMapsUrl(item) : null;
          const age = getAge(item.recorded_at);
          const isLive = age !== null && age <= STALE_AFTER_MS;
          return (
            <article key={item.reader_uid} className={`rounded-2xl border bg-white p-5 shadow-sm ${isBusReader(item) ? 'border-amber-200' : 'border-slate-200'}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {isBusReader(item) ? <Bus size={18} className="text-amber-600" /> : <Radio size={18} className="text-teal-600" />}
                    <h2 className="truncate font-bold text-slate-950">{item.reader_name || item.reader_uid}</h2>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">{item.reader_uid} • {item.device_type} • {checkpointLabel(item)}</p>
                  <p className="mt-1 text-sm text-slate-500">{item.location_name || 'No mapped campus location'}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${isLive ? 'bg-emerald-100 text-emerald-700' : hasGps ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                  {formatAge(item.recorded_at)}
                </span>
              </div>

              <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
                {hasGps ? (
                  <>
                    <p className="font-mono">{Number(item.latitude).toFixed(6)}, {Number(item.longitude).toFixed(6)}</p>
                    <p className="mt-1">Accuracy: {item.accuracy ? `${Math.round(item.accuracy)} m` : '-'}</p>
                  </>
                ) : (
                  <p>No GPS heartbeat received yet.</p>
                )}
              </div>

              {mapsUrl && appleUrl && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700"
                  >
                    <MapPin size={16} />
                    Google Maps
                    <Navigation size={14} />
                  </a>
                  <a
                    href={appleUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <ExternalLink size={16} />
                    Apple Maps
                  </a>
                </div>
              )}
            </article>
          );
        })}
      </section>
    </div>
  );
};

export default LiveTracking;
